import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Bot, Plus, Settings, Activity, Phone, MessageSquare, Calendar, Users, Brain, Zap, TrendingUp, Edit, Power, Trash2, Lock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const agentTypeConfig = {
  ai_receptionist: {
    label: 'Receptionist',
    description: 'Handles initial call routing and information gathering',
    icon: Phone,
    color: 'bg-blue-100 text-blue-600'
  },
  ai_service_support: {
    label: 'Service Support',
    description: 'Assists existing customers with inquiries',
    icon: Users,
    color: 'bg-green-100 text-green-600'
  },
  ai_sales_caller: {
    label: 'Sales Caller',
    description: 'Makes outbound sales and follow-up calls',
    icon: TrendingUp,
    color: 'bg-purple-100 text-purple-600'
  },
  ai_sales_qualifier: {
    label: 'Lead Qualifier',
    description: 'Qualifies and scores incoming leads',
    icon: Zap,
    color: 'bg-orange-100 text-orange-600'
  },
  ai_overflow: {
    label: 'Overflow Handler',
    description: 'Handles calls when human agents are busy',
    icon: Activity,
    color: 'bg-cyan-100 text-cyan-600'
  }
};

export default function CallCenterAIAgents() {
  const [agents, setAgents] = useState([]);
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    agent_type: 'ai_receptionist',
    persona_description: '',
    system_prompt: '',
    queue_ids: [],
    can_answer_calls: true,
    can_make_calls: false,
    can_send_sms: true,
    can_create_leads: true,
    can_schedule_appointments: true,
    temperature: 0.7,
    model: 'gpt-4o'
  });

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const [agentsRes, queuesRes] = await Promise.all([
        axios.get(`${API}/call-center/ai-agents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/call-center/queues`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAgents(agentsRes.data.agents || []);
      setQueues(queuesRes.data.queues || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load AI agents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createAgent = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/call-center/ai-agents`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('AI Agent created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (err) {
      console.error('Failed to create agent:', err);
      toast.error('Failed to create AI agent');
    }
  };

  const updateAgent = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(`${API}/call-center/ai-agents/${selectedAgent.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('AI Agent updated successfully');
      setShowEditDialog(false);
      setSelectedAgent(null);
      resetForm();
      fetchData();
    } catch (err) {
      console.error('Failed to update agent:', err);
      toast.error('Failed to update AI agent');
    }
  };

  const toggleAgentStatus = async (agent) => {
    const newStatus = agent.status === 'available' ? 'offline' : 'available';
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(`${API}/call-center/ai-agents/${agent.id}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Agent ${newStatus === 'available' ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (err) {
      console.error('Failed to update status:', err);
      toast.error('Failed to update agent status');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      agent_type: 'ai_receptionist',
      persona_description: '',
      system_prompt: '',
      queue_ids: [],
      can_answer_calls: true,
      can_make_calls: false,
      can_send_sms: true,
      can_create_leads: true,
      can_schedule_appointments: true,
      temperature: 0.7,
      model: 'gpt-4o'
    });
  };

  const editAgent = (agent) => {
    setSelectedAgent(agent);
    setFormData({
      name: agent.name,
      agent_type: agent.agent_type,
      persona_description: agent.persona_description || '',
      system_prompt: agent.system_prompt || '',
      queue_ids: agent.queue_ids || [],
      can_answer_calls: agent.can_answer_calls,
      can_make_calls: agent.can_make_calls,
      can_send_sms: agent.can_send_sms,
      can_create_leads: agent.can_create_leads,
      can_schedule_appointments: agent.can_schedule_appointments,
      temperature: agent.temperature || 0.7,
      model: agent.model || 'gpt-4o'
    });
    setShowEditDialog(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-500';
      case 'on_call': return 'bg-blue-500';
      case 'busy': return 'bg-yellow-500';
      case 'offline': return 'bg-gray-400';
      default: return 'bg-gray-400';
    }
  };

  const stats = {
    total: agents.length,
    online: agents.filter(a => a.status !== 'offline').length,
    totalInteractions: agents.reduce((sum, a) => sum + (a.total_interactions || 0), 0),
    avgSuccess: agents.length > 0 
      ? Math.round(agents.reduce((sum, a) => {
          if (a.total_interactions > 0) {
            return sum + (a.successful_interactions / a.total_interactions) * 100;
          }
          return sum;
        }, 0) / agents.filter(a => a.total_interactions > 0).length) || 0
      : 0
  };

  const AgentForm = ({ isEdit = false }) => (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
      <div>
        <Label>Agent Name</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g., Alex (AI Receptionist)"
        />
      </div>
      <div>
        <Label>Agent Type</Label>
        <Select
          value={formData.agent_type}
          onValueChange={(v) => setFormData({ ...formData, agent_type: v })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(agentTypeConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>
                <div className="flex items-center gap-2">
                  <config.icon className="h-4 w-4" />
                  {config.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">
          {agentTypeConfig[formData.agent_type]?.description}
        </p>
      </div>
      <div>
        <Label>Persona Description</Label>
        <Input
          value={formData.persona_description}
          onChange={(e) => setFormData({ ...formData, persona_description: e.target.value })}
          placeholder="Friendly and professional assistant"
        />
      </div>
      <div>
        <Label>System Prompt (Optional)</Label>
        <Textarea
          value={formData.system_prompt}
          onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
          placeholder="Custom instructions for this AI agent..."
          rows={4}
        />
        <p className="text-xs text-muted-foreground mt-1">
          Leave blank to use default prompt for this agent type
        </p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Model</Label>
          <Select
            value={formData.model}
            onValueChange={(v) => setFormData({ ...formData, model: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="gpt-4o">GPT-4o</SelectItem>
              <SelectItem value="gpt-4o-mini">GPT-4o Mini</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Temperature ({formData.temperature})</Label>
          <Input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={formData.temperature}
            onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) })}
            className="mt-2"
          />
        </div>
      </div>
      <div className="border rounded-lg p-4 space-y-3">
        <Label className="text-sm font-medium">Capabilities</Label>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-normal">Answer Calls</Label>
            <Switch
              checked={formData.can_answer_calls}
              onCheckedChange={(v) => setFormData({ ...formData, can_answer_calls: v })}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm font-normal">Make Calls</Label>
            <Switch
              checked={formData.can_make_calls}
              onCheckedChange={(v) => setFormData({ ...formData, can_make_calls: v })}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm font-normal">Send SMS</Label>
            <Switch
              checked={formData.can_send_sms}
              onCheckedChange={(v) => setFormData({ ...formData, can_send_sms: v })}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm font-normal">Create Leads</Label>
            <Switch
              checked={formData.can_create_leads}
              onCheckedChange={(v) => setFormData({ ...formData, can_create_leads: v })}
            />
          </div>
          <div className="flex items-center justify-between col-span-2">
            <Label className="text-sm font-normal">Schedule Appointments</Label>
            <Switch
              checked={formData.can_schedule_appointments}
              onCheckedChange={(v) => setFormData({ ...formData, can_schedule_appointments: v })}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
    <div className="p-6 space-y-6">
      {/* Phase 2 Preview Banner */}
      <Alert className="bg-cyan-50 border-cyan-200 dark:bg-cyan-900/20 dark:border-cyan-800">
        <Brain className="h-5 w-5 text-cyan-600" />
        <AlertDescription className="ml-2">
          <div className="flex items-center justify-between">
            <div>
              <span className="font-semibold text-cyan-800 dark:text-cyan-300">Phase 2 Prep Mode</span>
              <span className="text-cyan-700 dark:text-cyan-400 ml-2">
                Configure AI agents now. They will not handle live calls until Active Mode is enabled.
              </span>
            </div>
            <Badge variant="outline" className="border-cyan-500 text-cyan-700 dark:text-cyan-300">
              <Lock className="h-3 w-3 mr-1" />
              Agents Inactive
            </Badge>
          </div>
        </AlertDescription>
      </Alert>

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">AI Agents</h1>
          <p className="text-muted-foreground">Configure and manage AI-powered call center agents</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create AI Agent
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Agents</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Bot className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Online</p>
                <p className="text-2xl font-bold text-green-600">{stats.online}</p>
              </div>
              <Activity className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Interactions</p>
                <p className="text-2xl font-bold">{stats.totalInteractions}</p>
              </div>
              <Phone className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Success Rate</p>
                <p className="text-2xl font-bold">{stats.avgSuccess}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-orange-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Agents Grid */}
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : agents.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Bot className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No AI Agents Configured</h3>
            <p className="text-muted-foreground mb-4">
              Create your first AI agent to start automating call handling
            </p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Your First AI Agent
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {agents.map((agent) => {
            const config = agentTypeConfig[agent.agent_type] || agentTypeConfig.ai_receptionist;
            const Icon = config.icon;
            return (
              <Card key={agent.id} className="relative overflow-hidden">
                <div className={`absolute top-0 left-0 w-full h-1 ${agent.status === 'available' ? 'bg-green-500' : 'bg-gray-300'}`} />
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${config.color}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{agent.name}</CardTitle>
                        <CardDescription>{config.label}</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className={`w-2 h-2 rounded-full ${getStatusColor(agent.status)}`} />
                      <span className="text-xs text-muted-foreground capitalize">
                        {agent.status?.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {agent.persona_description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {agent.persona_description}
                    </p>
                  )}
                  
                  <div className="grid grid-cols-3 gap-2 text-center mb-3">
                    <div className="bg-muted/50 rounded p-2">
                      <p className="text-lg font-bold">{agent.total_interactions || 0}</p>
                      <p className="text-xs text-muted-foreground">Total</p>
                    </div>
                    <div className="bg-muted/50 rounded p-2">
                      <p className="text-lg font-bold text-green-600">
                        {agent.total_interactions 
                          ? Math.round((agent.successful_interactions / agent.total_interactions) * 100) 
                          : 0}%
                      </p>
                      <p className="text-xs text-muted-foreground">Success</p>
                    </div>
                    <div className="bg-muted/50 rounded p-2">
                      <p className="text-lg font-bold">{agent.escalated_interactions || 0}</p>
                      <p className="text-xs text-muted-foreground">Escalated</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1 mb-3">
                    {agent.can_answer_calls && (
                      <Badge variant="outline" className="text-xs">
                        <Phone className="h-3 w-3 mr-1" />Calls
                      </Badge>
                    )}
                    {agent.can_send_sms && (
                      <Badge variant="outline" className="text-xs">
                        <MessageSquare className="h-3 w-3 mr-1" />SMS
                      </Badge>
                    )}
                    {agent.can_schedule_appointments && (
                      <Badge variant="outline" className="text-xs">
                        <Calendar className="h-3 w-3 mr-1" />Schedule
                      </Badge>
                    )}
                  </div>

                  <div className="flex gap-2 pt-2 border-t">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => editAgent(agent)}>
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant={agent.status === 'available' ? 'secondary' : 'default'}
                      size="sm"
                      className="flex-1"
                      onClick={() => toggleAgentStatus(agent)}
                    >
                      <Power className="h-4 w-4 mr-1" />
                      {agent.status === 'available' ? 'Deactivate' : 'Activate'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create AI Agent</DialogTitle>
            <DialogDescription>Configure a new AI-powered agent for your call center</DialogDescription>
          </DialogHeader>
          <AgentForm />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>Cancel</Button>
            <Button onClick={createAgent} disabled={!formData.name}>Create Agent</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit AI Agent</DialogTitle>
            <DialogDescription>Modify agent configuration and capabilities</DialogDescription>
          </DialogHeader>
          <AgentForm isEdit />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); setSelectedAgent(null); resetForm(); }}>Cancel</Button>
            <Button onClick={updateAgent}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
